import React, { useCallback, useEffect, useState } from 'react';
import { Row, Typography, Empty, Badge } from 'antd';
import Moment from 'moment';
import Icon from '@ant-design/icons';
import { ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ReactComponent as CheckIcon } from 'icons/check-icon.svg';
import fetchKPIs from 'services/dashboard/dashboard';
import fetchSections from 'services/shared/section';

import DashboardHeader from './header';
import Metrics from './metric';
import PieChart from './chart/piechart';

import './dashboard.css';
import wsService from "../../../services/websocket/WebSocketService";

const { Text } = Typography;

const DashboardPage = () => {

	const [respondedKPIs, setRespondedKPIs] = useState([]);
	const [respondedSections, setRespondedSections] = useState([]);

	const [kpis, setKpis] = useState([]);
	const [sections, setSections] = useState([]);

	const [themeColor, setThemeColor] = useState({
		pending: '#A4B4CF',
		pendingSecond: '#F0F6FF',
		within: '#E76B73',
		withinSecond: '#FFE3E5',
		// morethan: '#0055FF',
		morethan: '#E76B73',
		morethanSecond: '#DFEAFF',
	});

	useEffect(() => {
		const fetchData = async () => {
			const today = Moment(new Date()).format('YYYYMMDD');

			const responseKPIs = await fetchKPIs(today);
			const sections = await fetchSections();

			setRespondedKPIs(responseKPIs);
			setRespondedSections(sections);

			const connectCategoryByDate = {
				FN: 'LIVEDATA',
				APP: 'LABHERO',
				FRM: 'DASHBOARD',
				SRCID: 'CLIENT',
				MODULE: 'CATEGORYBYDATE',
				VALUES: responseKPIs,
			};

			const connectSection = {
				FN: 'LIVEDATA',
				APP: 'LABHERO',
				FRM: 'DASHBOARD',
				SRCID: 'CLIENT',
				MODULE: 'SECTION',
				VALUES: sections,
			};

		};

		fetchData();
	}, []);

	// Listen for WebSocket responses
	useEffect(() => {
		const unsubscribe = wsService.subscribe((message) => {
			try {
				// Some servers send arrays, some send single objects
				const responses = Array.isArray(message) ? message : [message];

				responses.forEach((resp) => {
					if (resp.MODULE === 'SECTION') {
						setRespondedSections(resp.VALUE);
					} else if (resp.MODULE === 'CATEGORYBYDATE') {
						setRespondedKPIs(resp.VALUE);
					}
				});
			} catch (err) {
				console.error("Invalid WebSocket response:", message, err);
			}
		});

		return unsubscribe;
	}, []);

	const user = JSON.parse(sessionStorage.getItem('LOGGEDIN_USER_DATA'));


	useEffect(() => {
		if (respondedKPIs.length !== 0 && respondedSections.length !== 0) {
			const activeSectionsSorted = respondedSections
				.filter(section => section.isActive === 1)
				.sort((a, b) => (a.sectionSort || 0) - (b.sectionSort || 0));

			const kpiPending = respondedKPIs.find(item => item.category === 'PendingRequest');
			const kpiWithin = respondedKPIs.find(item => item.category === 'WithinTwoHours');
			const kpiMorethan = respondedKPIs.find(item => item.category === 'MoreThanTwoHours');

			const processKPIData = (kpi) => {
				if (!kpi) return [];
				return activeSectionsSorted.map(as => {
					const found = kpi.data.find(d => d.sectionCode === as.sectionCode);
					return {
						sectionCode: as.sectionCode,
						sectionName: as.sectionName,
						records: found ? found.records : 0,
					};
				});
			};

			const processedPending = processKPIData(kpiPending);
			const processedWithin = processKPIData(kpiWithin);
			const processedMorethan = processKPIData(kpiMorethan);

			const categoryTotals = {
				PendingRequest: processedPending.reduce((acc, curr) => acc + parseInt(curr.records, 10), 0),
				MoreThanTwoHours: processedMorethan.reduce((acc, curr) => acc + parseInt(curr.records, 10), 0),
				WithinTwoHours: processedWithin.reduce((acc, curr) => acc + parseInt(curr.records, 10), 0),
			};

			let combinedKPIs = [];

			combinedKPIs.push({
				...kpiPending,
				data: processedPending,
				label: 'PENDING',
				image: (
					<ClockCircleOutlined
						className="dashboard-metric-icon"
						style={{
							color: themeColor.pending,
						}}
					/>
				),
				color: themeColor.pending,
				secondColor: themeColor.pendingSecond,
				total: categoryTotals.PendingRequest,
			});

			combinedKPIs.push({
				...kpiMorethan,
				data: processedMorethan,
				label: 'MORE THAN 2 HOURS',
				image: (
					<ExclamationCircleOutlined
						className="dashboard-metric-icon"
						style={{
							color: themeColor.morethan,
						}}
					/>
				),
				color: themeColor.morethan,
				secondColor: themeColor.morethanSecond,
				total: categoryTotals.MoreThanTwoHours,
			});

			combinedKPIs.push({
				...kpiWithin,
				data: processedWithin,
				label: 'WITHIN 2 HOURS',
				image: <Icon component={CheckIcon} className="dashboard-metric-icon" />,
				color: themeColor.within,
				secondColor: themeColor.withinSecond,
				total: categoryTotals.WithinTwoHours,
			});

			const mappedSections = activeSectionsSorted.map(section => {
				const pending = processedPending.find(i => i.sectionCode === section.sectionCode);
				const within = processedWithin.find(i => i.sectionCode === section.sectionCode);
				const morethan = processedMorethan.find(i => i.sectionCode === section.sectionCode);

				return {
					sectionName: section.sectionName,
					pending: pending ? pending.records : 0,
					morethan: morethan ? morethan.records : 0,
					within: within ? within.records : 0,
				};
			});

			setKpis(combinedKPIs);
			setSections(mappedSections);
		}
	}, [respondedKPIs, respondedSections]);

	const MetricList = kpis.map((item, index) => (
		<Metrics
			key={index}
			image={item.image}
			value={item.value}
			label={item.label}
			data={item.data}
		/>
	));

	const PieCharts = sections.map((item, index) => {
		const hasNoData =
			Number(item.pending) === 0 && Number(item.morethan) === 0 && Number(item.within) === 0;

		const data = hasNoData
			? []
			: [
					{ item: 'Pending', count: Number(item.pending) },
					{ item: 'Morethan 2 Hours', count: Number(item.morethan) },
					{ item: 'Within 2 Hours', count: Number(item.within) },
			  ];

		const Placeholder = () => (
			<div className="piechart-no-data">
				<Empty
					description={
						<>
							<Text strong className="font-md">
								{item.sectionName}
							</Text>
							<br />
							<Text className="font-md">No Data</Text>
						</>
					}
				/>
			</div>
		);

		return (
			<div key={index}>
				{!hasNoData && (
					<Text className="font-md" strong>
						{item.sectionName}
					</Text>
				)}
				<PieChart data={data} placeHolder={<Placeholder />} />
			</div>
		);
	});

	return (
		<div>
			<div className="dashboard-main-wrapper">
				<Row>
					<DashboardHeader user={user ? user.givenName : null} />
				</Row>
				<div className="main-pane">
					<div className="metric-list">{MetricList}</div>
					<div>
						<div className="legend-con">
							<div>
								<Text strong>LEGEND:</Text>
							</div>
							<div>
								<Badge color="#CBDEFF" text="Pending" />
							</div>
							<div>
								<Badge color="#6395F9" text="Within 2 Hours" />
							</div>
							<div>
								<Badge color="#ff7979" text="Morethan 2 Hours" />
							</div>
						</div>
						<div className="chart-con">{PieCharts}</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DashboardPage;
